import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ContactForm() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Generel forespørgsel',
    message: '',
    website_url: '', // Honeypot
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: 'Generel forespørgsel', message: '', website_url: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (status === 'success') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 p-8 rounded-2xl text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-emerald-900 mb-2">Tak for din besked!</h3>
        <p className="text-sm sm:text-base text-emerald-700">Jeg har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
        <button 
          onClick={() => setStatus('idle')}
          className="mt-6 text-emerald-600 font-semibold hover:text-emerald-800 transition-colors"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Honeypot - hidden from humans */}
      <div className="sr-only absolute opacity-0 -z-10 select-none pointer-events-none">
        <label htmlFor="website_url">Website URL</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="text-xs sm:text-sm font-semibold text-blue-900">Navn</label>
          <input
            required
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-white text-sm"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="text-xs sm:text-sm font-semibold text-blue-900">E-mail</label>
          <input
            required
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="din@email.dk"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-white text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="phone" className="text-xs sm:text-sm font-semibold text-blue-900">Telefon</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Dit telefonnummer"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-white text-sm"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="subject" className="text-xs sm:text-sm font-semibold text-blue-900">Emne</label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-white text-sm"
          >
            <option>Generel forespørgsel</option>
            <option>Booking spørgsmål</option>
            <option>Spørgsmål om behandlinger</option>
            <option>Andet</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="text-xs sm:text-sm font-semibold text-blue-900">Besked</label>
        <textarea
          required
          id="message"
          name="message"
          rows="5"
          value={formData.message}
          onChange={handleChange}
          placeholder="Hvordan kan jeg hjælpe dig?"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none bg-white resize-none text-sm"
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="flex items-center text-red-600 bg-red-50 p-4 rounded-xl border border-red-100">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p className="text-xs sm:text-sm">Der skete en fejl. Prøv venligst igen senere eller ring til mig.</p>
        </div>
      )}

      <button
        disabled={status === 'loading'}
        type="submit"
        className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
      >
        {status === 'loading' ? (
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        ) : (
          <>
            <span>Send besked</span>
            <Send className="w-5 h-5" />
          </>
        )}
      </button>
    </form>
  );
}
